/**
 * Callout
 *
 * Encadré d'information contextuelle affiché dans le flux d'une page : état d'une donnée,
 * avertissement, confirmation. Non bloquant et non éphémère (contrairement à un toast).
 */

import type { ComponentType, ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const calloutVariants = cva('rounded-lg border p-4', {
  variants: {
    variant: {
      info: 'bg-primary-50 border-primary-200 text-primary-900',
      success: 'bg-success-50 border-success-200 text-success-900',
      warning: 'bg-warning-50 border-warning-200 text-warning-900',
      danger: 'bg-danger-50 border-danger-200 text-danger-900',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
});

type CalloutVariant = NonNullable<VariantProps<typeof calloutVariants>['variant']>;

const ICONS: Record<CalloutVariant, ComponentType<{ className?: string }>> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
};

const ICON_COLORS: Record<CalloutVariant, string> = {
  info: 'text-primary-600',
  success: 'text-success-600',
  warning: 'text-warning-600',
  danger: 'text-danger-600',
};

export interface CalloutProps {
  /** @default 'info' */
  variant?: CalloutVariant;
  title?: ReactNode;
  children?: ReactNode;
  /** Icône personnalisée ; `null` pour n'en afficher aucune */
  icon?: ReactNode | null;
  /** Boutons alignés à droite */
  actions?: ReactNode;
  className?: string;
}

export function Callout({ variant = 'info', title, children, icon, actions, className }: CalloutProps) {
  const DefaultIcon = ICONS[variant];

  return (
    <div
      role={variant === 'warning' || variant === 'danger' ? 'alert' : 'status'}
      className={cn(calloutVariants({ variant }), className)}
    >
      <div className="flex gap-3">
        {icon === null
          ? null
          : icon ?? <DefaultIcon className={cn('w-5 h-5 flex-shrink-0 mt-0.5', ICON_COLORS[variant])} />}
        {/* Sous `sm`, les actions passent SOUS le texte (alignees avec lui,
            a droite de l'icone) au lieu de l'ecraser ; a partir de `sm`,
            meme ligne qu'avant (meme espacement `gap-3`). */}
        <div className="flex flex-1 min-w-0 gap-3 max-sm:flex-col">
          <div className="flex-1 min-w-0 text-sm">
            {title && <p className="font-medium">{title}</p>}
            {children && <div className={cn(title ? 'mt-1' : undefined)}>{children}</div>}
          </div>
          {actions && (
            <div className="flex flex-shrink-0 items-start gap-2 max-sm:flex-wrap">{actions}</div>
          )}
        </div>
      </div>
    </div>
  );
}
